<script lang="ts">
  import { onDestroy, onMount } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import { mdiContentCopy } from "@mdi/js"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import type { ProjectTableItem } from "@kontextwerk/control-web-client"
  import { copyTextToClipboard } from "@kontextwerk/web-sdk/core/platform/clipboard"
  import { buildProjectEmbedSnippet } from "@kontextwerk/control-web-frame/shared/logic/widget-snippet"
  import { fetchOrganizationProjectTable } from "../../../../../../../../../../api/kehrblatt/projects"
  import { resolvePlanningChatWidgetProject } from "./_logic/chatWidget"

  const { organizationId } = $props<{
    organizationId: string
  }>()

  let project = $state<ProjectTableItem | null>(null)
  let copying = $state(false)
  let disposed = false
  let loadRequest = 0

  const embedSnippet = $derived(buildProjectEmbedSnippet(project))

  const loadProject = async () => {
    const request = ++loadRequest
    try {
      const projects = await fetchOrganizationProjectTable(organizationId)

      if (disposed || request !== loadRequest) return

      project = resolvePlanningChatWidgetProject(projects)
    } catch (err) {
      if (disposed || request !== loadRequest) return

      console.error("kehrblatt_chat_widget_project_load_failed", err)
      project = null
    }
  }

  onMount(() => {
    void loadProject()
  })

  onDestroy(() => {
    disposed = true
    loadRequest += 1
  })

  const copySnippet = async () => {
    if (!embedSnippet || copying) return

    copying = true
    try {
      await copyTextToClipboard(embedSnippet)
      newNotification({ class: "success", html: "Snippet in die Zwischenablage kopiert." })
    } catch (err) {
      console.error("chat_widget_snippet_copy_failed", err)
      newNotification({ class: "error", html: "Snippet konnte nicht kopiert werden." })
    } finally {
      copying = false
    }
  }
</script>

<section class="chat-widget-section" aria-label="Chat-Widget">
  <SectionTitle
    title="Chat-Widget"
    description="Nutzen Sie dieses Snippet, wenn Sie den Chatbot in Ihrer Website einbinden wollen."
    brightBackground={true}
    small={true}
    helpModalTextId="organizationChatWidget"
  />

  {#if project && embedSnippet}
    <ActionBox label="Einbindungs-Snippet">
      <div class="snippet-content">
        <p>
          Wenn Sie dieses Snippet in das HTML-Dokument Ihrer Website einfügen, erscheint der Chatbot
          unten rechts auf Ihrer Website. Besucher können den Chatbot dort öffnen und zur
          Terminabstimmung nutzen.
        </p>
        <pre><code>{embedSnippet}</code></pre>
      </div>
      {#snippet buttonSnippet()}
        <CTA
          type="secondaryButton"
          content={copying ? "Kopiere…" : "Snippet kopieren"}
          icon={mdiContentCopy}
          onClick={copySnippet}
          disabled={copying}
          small={true}
        />
      {/snippet}
    </ActionBox>
  {:else}
    <ActionBox label="Einbindungs-Snippet">
      <p class="empty-state">Für diese Organisation ist noch kein Chatbot-Projekt verfügbar.</p>
    </ActionBox>
  {/if}
</section>

<style lang="less">
  .chat-widget-section {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
  }

  .snippet-content {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    color: var(--text-invers-100);

    p {
      margin: 0;
      color: var(--text-invers-100);
    }

    pre {
      margin: 0;
      max-width: 100%;
      overflow-x: auto;
      padding: 0.75rem;
      border: 1px solid color-mix(in srgb, var(--text-invers-100) 40%, transparent);
      background: color-mix(in srgb, var(--text-invers-100) 6%, transparent);
      color: var(--text-invers-100);
      font-size: 0.72rem;
      line-height: 1.45;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }

  .empty-state {
    margin: 0;
    color: var(--text-invers-100);
  }
</style>
